/**
 * GLSL Shaders for Wind Particle System
 *
 * Six shader programs:
 * 1. UPDATE   — moves particles through the wind field (transform feedback)
 * 2. DRAW     — renders particles as line-segment quads into the trail FBO
 * 3. FADE     — draws previous trail with reduced opacity (trail decay)
 * 4. SCREEN   — composites the trail texture onto the map (with globe mask)
 * 5. HEATMAP  — renders a semi-transparent wind speed color field on the map
 */

// ─── 1. UPDATE SHADERS (transform feedback) ─────────────────────────

export const UPDATE_VERT = `#version 300 es
precision highp float;

in vec2 a_position;
in vec2 a_prevPosition;
in float a_age;
in float a_speed;

out vec2 v_position;
out vec2 v_prevPosition;
out float v_age;
out float v_speed;

uniform sampler2D u_windTexture;
uniform float u_uMin;
uniform float u_uMax;
uniform float u_vMin;
uniform float u_vMax;
uniform float u_speedFactor;
uniform float u_maxAge;
uniform float u_randomSeed;
uniform bool u_isGlobal;

float rand(vec2 co) {
  return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec4 windSample = texture(u_windTexture, a_position);
  float u = mix(u_uMin, u_uMax, windSample.r);
  float v = mix(u_vMin, u_vMax, windSample.g);
  float speed = length(vec2(u, v));

  float newAge = a_age + 1.0;
  bool shouldRespawn = newAge > u_maxAge || speed < 0.01;

  if (shouldRespawn) {
    float rx = rand(vec2(a_position.x + u_randomSeed, a_position.y));
    float ry = rand(vec2(a_position.y + u_randomSeed, a_position.x + 0.5));
    v_position = vec2(rx, ry);
    v_prevPosition = vec2(rx, ry);
    v_age = 0.0;
    v_speed = 0.0;
  } else {
    v_prevPosition = a_position;

    float dx = u * u_speedFactor * 0.001;
    float dy = v * u_speedFactor * 0.001;
    vec2 newPos = a_position + vec2(dx, -dy);

    if (u_isGlobal) {
      float oldX = newPos.x;
      newPos.x = fract(newPos.x + 1.0);
      newPos.y = clamp(newPos.y, 0.0, 1.0);
      // If longitude wrapped around, reset prev to avoid straight line across globe
      if (abs(newPos.x - oldX) > 0.5) {
        v_prevPosition = newPos;
      }
    } else {
      if (newPos.x < 0.0 || newPos.x > 1.0 || newPos.y < 0.0 || newPos.y > 1.0) {
        float rx = rand(vec2(newPos.x + u_randomSeed, newPos.y));
        float ry = rand(vec2(newPos.y + u_randomSeed, newPos.x + 0.3));
        newPos = vec2(rx, ry);
        v_prevPosition = newPos;
        newAge = 0.0;
        speed = 0.0;
      }
    }

    v_position = newPos;
    v_age = newAge;
    v_speed = speed;
  }
}
`;

export const UPDATE_FRAG = `#version 300 es
precision highp float;
out vec4 fragColor;
void main() {
  fragColor = vec4(0.0);
}
`;

// ─── 2. DRAW SHADERS (instanced line quads into trail FBO) ──────────

export const DRAW_VERT = `#version 300 es
precision highp float;

// Per-vertex: template quad corner
in vec2 a_quad; // (-1,-1), (-1,1), (1,-1), (1,1)

// Per-instance: particle data (divisor 1)
in vec2 a_position;
in vec2 a_prevPosition;
in float a_age;
in float a_speed;

uniform mat4 u_matrix;
uniform float u_lineWidth;
uniform float u_maxAge;
uniform float u_maxSpeed;
uniform vec2 u_resolution;

uniform float u_boundsWest;
uniform float u_boundsEast;
uniform float u_boundsNorth;
uniform float u_boundsSouth;

out float v_speed;
out float v_alpha;
out float v_across;

vec2 normalizedToMercator(vec2 pos) {
  float lng = mix(u_boundsWest, u_boundsEast, pos.x);
  float lat = mix(u_boundsNorth, u_boundsSouth, pos.y);
  lat = clamp(lat, -85.05, 85.05);

  float mercX = (lng + 180.0) / 360.0;
  float latRad = radians(lat);
  float mercY = (1.0 - log(tan(latRad) + 1.0 / cos(latRad)) / 3.141592653589793) * 0.5;

  return vec2(mercX, mercY);
}

void main() {
  vec2 mercA = normalizedToMercator(a_prevPosition);
  vec2 mercB = normalizedToMercator(a_position);

  vec4 clipA = u_matrix * vec4(mercA, 0.0, 1.0);
  vec4 clipB = u_matrix * vec4(mercB, 0.0, 1.0);

  // Globe clipping: both endpoints behind the globe
  if (clipA.w <= 0.0 && clipB.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    v_alpha = 0.0;
    v_speed = 0.0;
    v_across = 0.0;
    return;
  }

  // Perspective divide to NDC
  vec2 ndcA = clipA.xy / clipA.w;
  vec2 ndcB = clipB.xy / clipB.w;

  // NDC to screen pixels
  vec2 screenA = (ndcA * 0.5 + 0.5) * u_resolution;
  vec2 screenB = (ndcB * 0.5 + 0.5) * u_resolution;

  // Line direction in screen space
  vec2 dir = screenB - screenA;
  float len = length(dir);

  // Skip absurdly long segments (wrap-around artifacts)
  if (len > 200.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    v_alpha = 0.0;
    v_speed = 0.0;
    v_across = 0.0;
    return;
  }

  if (len < 0.001) {
    dir = vec2(1.0, 0.0);
    len = 0.0;
  } else {
    dir /= len;
  }

  // Ensure minimum visible line length (at least lineWidth * 1.5)
  float minLen = u_lineWidth * 1.5;
  if (len < minLen && a_speed > 0.0) {
    vec2 center = (screenA + screenB) * 0.5;
    screenA = center - dir * minLen * 0.5;
    screenB = center + dir * minLen * 0.5;
  }

  // Perpendicular direction
  vec2 normal = vec2(-dir.y, dir.x);

  // a_quad.x: -1 = prev endpoint, +1 = current endpoint
  float t = a_quad.x * 0.5 + 0.5;
  vec2 screenPos = mix(screenA, screenB, t);

  // Speed-based width: calm winds 40%, strong winds 100%
  float speedNorm = clamp(a_speed / u_maxSpeed, 0.0, 1.0);
  float dynamicWidth = u_lineWidth * (0.4 + 0.6 * smoothstep(0.0, 0.5, speedNorm));

  // a_quad.y: -1 = left edge, +1 = right edge
  screenPos += normal * a_quad.y * dynamicWidth * 0.5;

  // Back to NDC
  vec2 ndc = (screenPos / u_resolution) * 2.0 - 1.0;

  // Interpolate clip z/w for proper depth
  float w = mix(clipA.w, clipB.w, t);
  float z = mix(clipA.z, clipB.z, t);

  gl_Position = vec4(ndc * w, z, w);

  v_across = a_quad.y;
  v_speed = a_speed;

  // Cubic age fade: stays bright longer, fades at end of life
  float ageFraction = a_age / u_maxAge;
  v_alpha = 1.0 - ageFraction * ageFraction * ageFraction;
  v_alpha *= smoothstep(0.0, 0.5, a_speed);
}
`;

export const DRAW_FRAG = `#version 300 es
precision highp float;

in float v_speed;
in float v_alpha;
in float v_across;

uniform sampler2D u_colorRamp;
uniform float u_maxSpeed;

out vec4 fragColor;

void main() {
  if (v_alpha < 0.01) discard;

  float speedNorm = clamp(v_speed / u_maxSpeed, 0.0, 1.0);
  vec4 color = texture(u_colorRamp, vec2(speedNorm, 0.5));

  // Soft alpha edge across line width (brushstroke effect)
  float edgeAlpha = 1.0 - abs(v_across);
  edgeAlpha = smoothstep(0.0, 0.7, edgeAlpha);

  // Speed-based brightness boost: strong winds glow
  float brightness = 1.0 + smoothstep(0.3, 1.0, speedNorm) * 0.8;

  float finalAlpha = color.a * v_alpha * edgeAlpha;
  fragColor = vec4(color.rgb * brightness, finalAlpha);
}
`;

// ─── 3. FADE SHADER (trail decay) ──────────────────────────────────

export const FADE_VERT = `#version 300 es
precision highp float;

layout(location = 0) in vec2 a_pos;
out vec2 v_uv;

void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

export const FADE_FRAG = `#version 300 es
precision highp float;

in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_fadeOpacity;

out vec4 fragColor;

void main() {
  vec4 color = texture(u_texture, v_uv);
  fragColor = color * u_fadeOpacity;
}
`;

// ─── 4. SCREEN SHADER (composite trail onto map with globe mask) ────

export const SCREEN_VERT = `#version 300 es
precision highp float;

layout(location = 0) in vec2 a_pos;
out vec2 v_uv;

void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

export const SCREEN_FRAG = `#version 300 es
precision highp float;

in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_opacity;

// Globe clipping
uniform vec2 u_globeCenter;
uniform float u_globeRadius;
uniform vec2 u_resolution;
uniform bool u_isGlobe;

out vec4 fragColor;

void main() {
  vec4 color = texture(u_texture, v_uv);

  if (u_isGlobe) {
    vec2 fragPixel = vec2(v_uv.x * u_resolution.x, (1.0 - v_uv.y) * u_resolution.y);
    float dist = distance(fragPixel, u_globeCenter);
    if (dist > u_globeRadius) discard;

    float edge = 1.0 - smoothstep(u_globeRadius - 2.0, u_globeRadius, dist);
    float a = color.a * u_opacity * edge;
    // Premultiplied alpha output (for ONE, ONE_MINUS_SRC_ALPHA blend)
    fragColor = vec4(color.rgb * a, a);
    return;
  }

  float a = color.a * u_opacity;
  fragColor = vec4(color.rgb * a, a);
}
`;

// ─── 5. HEATMAP SHADER (wind speed background color field) ──────────

export const HEATMAP_VERT = `#version 300 es
precision highp float;

in vec2 a_pos;

uniform mat4 u_matrix;
uniform float u_boundsWest;
uniform float u_boundsEast;
uniform float u_boundsNorth;
uniform float u_boundsSouth;

out vec2 v_uv;
out float v_clipW;

void main() {
  v_uv = a_pos;

  float lng = mix(u_boundsWest, u_boundsEast, a_pos.x);
  float lat = mix(u_boundsNorth, u_boundsSouth, a_pos.y);
  lat = clamp(lat, -85.05, 85.05);

  float mercX = (lng + 180.0) / 360.0;
  float latRad = radians(lat);
  float mercY = (1.0 - log(tan(latRad) + 1.0 / cos(latRad)) / 3.141592653589793) * 0.5;

  gl_Position = u_matrix * vec4(mercX, mercY, 0.0, 1.0);
  v_clipW = gl_Position.w;
}
`;

export const HEATMAP_FRAG = `#version 300 es
precision highp float;

in vec2 v_uv;
in float v_clipW;

uniform sampler2D u_windTexture;
uniform sampler2D u_colorRamp;
uniform float u_uMin;
uniform float u_uMax;
uniform float u_vMin;
uniform float u_vMax;
uniform float u_maxSpeed;
uniform float u_opacity;

// Globe clipping (same as screen shader)
uniform vec2 u_globeCenter;
uniform float u_globeRadius;
uniform vec2 u_resolution;
uniform bool u_isGlobe;

out vec4 fragColor;

void main() {
  // Globe back-face culling
  if (v_clipW <= 0.0) discard;

  // Globe circle mask — clip fragments outside visible globe
  if (u_isGlobe) {
    vec2 fragPixel = vec2(gl_FragCoord.x, u_resolution.y - gl_FragCoord.y);
    float dist = distance(fragPixel, u_globeCenter);
    if (dist > u_globeRadius) discard;
  }

  vec4 wind = texture(u_windTexture, v_uv);
  float u = mix(u_uMin, u_uMax, wind.r);
  float v = mix(u_vMin, u_vMax, wind.g);
  float speed = length(vec2(u, v));

  float speedNorm = clamp(speed / u_maxSpeed, 0.0, 1.0);
  vec4 color = texture(u_colorRamp, vec2(speedNorm, 0.5));

  // Stronger winds more visible, calm areas nearly transparent
  float intensity = smoothstep(0.0, 0.3, speedNorm) * 0.7 + 0.3;
  fragColor = vec4(color.rgb, color.a * u_opacity * intensity);
}
`;
